// PLEX-6049: Bedrock citations (CitationsDelta) on a chat turn, shared by the classic chat
// (Turn.js) and the v2 card (ConversationView.js).
//
// What Bedrock streams for a cited reply (observed on a real converse_stream): each citation
// arrives complete, in its own content block, followed by the sentence it supports in that
// block; the text between citations ("\n\n2. ") sits in blocks of its own. LLMClient keeps a
// cited block as {text, citations: [...]} -- a list, since one sentence can carry several
// citations (Bedrock's CitationsContentBlock holds a list too).
//
// So a cited reply is ONE text split across consecutive blocks: its pieces join as-is (a
// blank-line join would break "1. " away from its sentence), each citation gets a footnote
// marker [n] after the sentence it supports, and the sources are listed under the reply in
// the same order.

export const isWebUrl = (s) => typeof s === "string" && /^https?:\/\//i.test(s);

// The citations on one block. `citation` is the single-citation form older turns may carry.
export const blockCitations = (block) =>
  Array.isArray(block?.citations) ? block.citations : (block?.citation ? [block.citation] : []);

export const hasCitations = (blocks) =>
  Array.isArray(blocks) && blocks.some((b) => blockCitations(b).length > 0);

// A turn's citations in footnote order: footnote n is citations[n - 1].
export const turnCitations = (blocks) => (blocks || []).flatMap(blockCitations);

const isTextual = (b) => b && (b.text != null || blockCitations(b).length > 0);

// The text of a cited reply. With markers (for display) each citation's [n] follows its
// sentence; without (copy, previews) it is the plain text.
export const citedText = (blocks, {markers = true} = {}) => {
  let n = 0;
  return (blocks || []).filter(isTextual).map((b) => {
    const text = b.text || "";
    const count = blockCitations(b).length;
    if (!markers || count === 0) return text;
    return `${text} ${Array.from({length: count}, () => `\\[${++n}\\]`).join("")}`;
  }).join("");
};

// A turn's blocks for display: its text and cited blocks become ONE text block (at the first
// one's position) carrying the footnote markers; other blocks (tool calls, reasoning) stay put.
export const mergeCitedBlocks = (blocks) => {
  const textual = blocks.filter(isTextual);
  const merged = {text: citedText(blocks), partial: textual.some((b) => b.partial)};
  if (textual.some((b) => b.suppressFinalAnswer)) merged.suppressFinalAnswer = true;
  const first = blocks.findIndex(isTextual);
  return blocks.flatMap((b, i) => (i === first ? [merged] : isTextual(b) ? [] : [b]));
};

// One source's line: title (or web source), page, and the quoted passage.
export const citationLabel = (citation) => {
  const {title, source, sourceContent, location} = citation || {};
  const label = title || (isWebUrl(source) ? source : null) || "Source";
  // documentPage.start is the cited page (Bedrock reports pages as a start/end range).
  const page = location?.documentPage?.start != null ? ` (p. ${location.documentPage.start})` : "";
  const quote = (sourceContent || []).map((c) => c?.text || "").join(" ").replace(/\s+/g, " ").trim();
  return quote ? `${label}${page}: “${quote}”` : `${label}${page}`;
};
